import { twMerge } from 'tailwind-merge';
import { useSelector } from 'react-redux';
import TierLevelSvg from './TierLevelsvg';
import React, { useEffect, useRef, useState } from 'react';
import { ReducerState } from '@/services/common/lib/reducerState';
import useGetUserTier from '@/services/user/queries/useGetUserTier';
import LoadingSpinner from '@/components/LoadingSpinner/LoadingSpinner';

import { tierTextColor } from '../lib/tierTextColor';
import TierIcon from './TierIcon';
import RewardsModal from '@/features/rewards/RewardsModal';

const TierLevel = () => {
  const remToPixels = (rem: number) => {
    return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
  };

  const [size, setSize] = useState({ width: 0, height: 0 });
  const observedDivRef = useRef(null);

  const [showRewardsModal, setShowRewardsModal] = useState(false);

  const user = useSelector((state: ReducerState) => state.auth);

  const { isSuccess, isLoading, data } = useGetUserTier({
    userId: user.id || '',
  });

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;

        // Add padding to the width and height
        // Assuming p-2 is 0.5rem and 1rem = 16px, so p-2 = 8px
        const paddingSize = remToPixels(1); // 8px * 2 (for both sides)
        setSize({
          width: width + paddingSize,
          height: height + paddingSize,
        });
      }
    });

    if (observedDivRef.current) {
      resizeObserver.observe(observedDivRef.current);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [isSuccess]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isSuccess || !data) {
    return null;
  }

  const tierData = data.data;

  const handleShowRewards = () => setShowRewardsModal(true);

  return (
    <div className="w-full">
      {tierData && (
        <div className="relative min-h-24 w-full cursor-pointer" onClick={handleShowRewards}>
          <div className="absolute inset-0 min-h-24">
            {size.width === 0 || size.height === 0 ? (
              <div
                className="h-full w-full rounded-[1.5rem] border border-solid border-pn-violet-light"
                style={{ borderWidth: '4px' }}
              />
            ) : (
              <TierLevelSvg
                progress={tierData.tierProgressPercentage || 0}
                width={size.width}
                height={size.height}
                borderRadius="1.5rem"
              />
            )}
          </div>
          <div className="min-h-24 p-2" ref={observedDivRef}>
            <div className="h-full w-full rounded-[1rem] bg-phenom-background-gradient-gray-violet-dark p-0.5">
              <div
                className="flex h-20 w-full items-center justify-center gap-3 rounded-[1rem] bg-pn-violet-dark p-1 align-middle"
                id="tierCard"
              >
                <TierIcon tier={tierData.currentTier?.tier} />
                <div className="flex flex-col">
                  <span className="font-manrope text-xs font-semibold leading-[16px] text-ph-gray-evening">
                    Current Tier:
                  </span>

                  <span className="mb-1 font-manrope text-lg font-semibold leading-[24px] text-ph-gray-white">
                    {tierData.currentTier?.name}
                  </span>
                  {tierData.nextTier && (
                    <div className="flex items-center gap-1">
                      <p className="text-xs font-medium text-ph-gray-evening">Next:</p>
                      <p className={twMerge(tierTextColor(tierData.nextTier?.tier), 'text-xs')}>
                        {tierData.nextTier?.name}
                      </p>

                      <TierIcon tier={tierData.nextTier?.tier} className="h-4 w-4" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <RewardsModal open={showRewardsModal} setClose={() => setShowRewardsModal(false)} defaultTab={1} />
        </div>
      )}
    </div>
  );
};

export default TierLevel;
